import {
  Box,
  Button,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import Image from "next/image";
import { getProduct, getProducts } from "../../lib/woocommerce/api";
import { Product } from "../../types/woocommerce";
import { PageContainer } from "../../components/Layout/PageContainer";
import { LocalMallOutlined } from "@mui/icons-material";
import { ShopActionType } from "../../context/shop/shop.types";
import { useShopContext } from "../../context/shop/shop.context";
import { useTranslation } from "react-i18next";
import FooterBar from "../../components/Blog/FooterBar";
import { GetStaticProps } from "next";
import { useState } from "react";
import Seo from "../../components/others/Seo";
import Breadscrumb from "../../components/Blog/Breadscrumb";
import TitleBar from "../../components/Blog/Titlebar";
import { useMySnackbar } from "../../hooks/useMySnackbar";
import NextLink from "../../components/others/NextLink";

const Product = ({ product }: { product: Product }) => {
  const featuredImage = product.images?.[0];
  const theme = useTheme();
  const { shopDispatch } = useShopContext();
  const { displaySuccessSnackbar, closeSnackbar } = useMySnackbar();
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  return (
    <PageContainer>
      <Seo
        title={product?.name ?? undefined}
        ogImage={featuredImage.src ?? undefined}
        description={
          product.short_description ?? product.description ?? undefined
        }
      />
      <TitleBar title={product.name} />
      <Breadscrumb
        paths={[
          { title: "Shop", url: "/shop" },
          {
            title: product.name,
            url: `/shop/${product.id}_${product.slug}`,
          },
        ]}
      />
      <Stack
        maxWidth="lg"
        sx={{
          width: "100%",
          flexDirection: { md: "row", xs: "column" },
          my: 3,
          px: { md: 0, xs: 2 },
        }}
      >
        <Stack flex={1} mt={2}>
          {featuredImage?.src && (
            <Box
              sx={{
                position: "relative",
                height: 400,
              }}
            >
              <Image
                src={featuredImage?.src}
                alt={featuredImage?.alt ?? ""}
                fill
                priority
                sizes={`(max-width: ${theme.breakpoints.values.xs}px) 100vw,
                ${theme.breakpoints.values.md}px) 50vw,  25vw`}
                style={{ objectFit: "contain", objectPosition: "center" }}
              />
            </Box>
          )}
        </Stack>
        <Card
          sx={{
            flex: 1,
            p: 2,
            mt: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack
            sx={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h4">Prix</Typography>
            <Typography variant="h3">
              {parseFloat(product.price).toFixed(2)}€
            </Typography>
          </Stack>
          <Stack flex={1} sx={{ pb: 2 }}>
            <div
              dangerouslySetInnerHTML={{
                __html: product.description,
              }}
            />
          </Stack>
          <Stack flexDirection="row">
            <FormControl fullWidth>
              <InputLabel>{t("articles.quantity")}</InputLabel>
              <Select
                id="quantity-select"
                value={quantity}
                label={t("articles.quantity")}
                onChange={(event) => setQuantity(event.target.value as number)}
              >
                {[...new Array(4)].map((_, index) => (
                  <MenuItem key={index} value={index + 1}>
                    {index + 1}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="contained"
              sx={{ ml: 2 }}
              onClick={() => {
                shopDispatch({
                  type: ShopActionType.ADD_ITEM,
                  payload: { item: { product, quantity } },
                });
                displaySuccessSnackbar(t("snackbar.add_item"), (id) => (
                  <NextLink href="/shop/cart">
                    <Button
                      onClick={() => closeSnackbar(id)}
                      variant="contained"
                    >
                      {t("snackbar.see_cart")}
                    </Button>
                  </NextLink>
                ));
              }}
            >
              <LocalMallOutlined sx={{ mr: 1 }} />
              <Typography variant="body2">{t("articles.add_cart")}</Typography>
            </Button>
          </Stack>
        </Card>
      </Stack>
      <FooterBar />
    </PageContainer>
  );
};

// This function gets called at build time
export async function getStaticPaths() {
  const products = await getProducts();

  const paths = products
    .filter(({ id }) => !!id)
    .map(({ id, slug }) => ({
      params: { id_slug: `${id.toString()}_${slug}` },
    }));
  // { fallback: false } means other routes should 404.
  return { paths, fallback: false };
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  //TODO : check undefined
  const id_slug = params?.id_slug as string;
  const id = id_slug.split("_")[0];
  const product = await getProduct({ id });
  return {
    props: {
      product,
    },
  };
};

export default Product;
